/**
 * Revolut wizard — the flow's own cross-package write-intent contract, and
 * nothing else. The wizard's fields are component-local @state() on
 * revolut-wizard.view.ts itself, so there is no shared multi-field draft to
 * declare here the way the old four-step design needed one.
 *
 * `revolutSaveIntent$` is the one thing this file exists to carry: the
 * wizard's Save & verify action leaves this package through it, and
 * web/src/admin/settings/integrations/revolut/wizard.sync.ts is the
 * sole subscriber, forwarding straight into the EXISTING
 * saveIntegrationAction$ (web's integrations.state.ts) — this
 * package can never reach that subject itself, since it cannot import an
 * app-workspace file.
 *
 * `secretKey` is optional on this payload: the wizard omits the KEY entirely
 * when the operator left the field blank on an environment that already
 * holds a stored key, which is the contract's own "keep whatever is stored"
 * signal (SaveIntegrationInput's every declared secret field is `.nullish()`,
 * and every one of them is `.min(1)` — so `""` is rejected, only absence
 * means keep). That is what lets an operator activate, change the currency,
 * or set the Apple Pay domain without re-pasting a key nothing ever shows
 * back to them.
 *
 * Beyond `secretKey`, the payload carries this save's operational inputs —
 * `activate`/`publicKey`/`currency`/`applePayDomain`, mirroring
 * SaveIntegrationInput's own revolutSandbox/revolutLive variants
 * (@momo/types) field-for-field. `RevolutOperationalFields` derives them
 * from that canonical contract rather than re-typing each field by hand, so
 * a contract change surfaces here as a type error, not silent drift.
 * `applePayDomain` is always present on this Subject's own payload shape
 * (both envs) — wizard.sync.ts is the seam that omits the key entirely for
 * revolutSandbox, since only the revolutLive contract variant declares it
 * and the receiving `.strict()` parse rejects an unrecognized key.
 *
 * `RevolutEnvId` is fixed by whichever row opened the wizard — there is no
 * in-wizard picker.
 */
import type { Integrations, SaveIntegration } from "@momo/types";
import { Subject } from "rxjs";
import type { IntegrationStatusLine } from "../status.state.ts";

/** The caller (the card that opens this wizard) fixes which environment;
 *  there is no in-wizard picker. */
export type RevolutEnvId = Extract<
	Integrations.IntegrationId,
	"revolutSandbox" | "revolutLive"
>;

/** The shared status shape under this wizard's own former name, kept solely
 *  because the design studio's revolut-wizard fixtures import it; nothing in
 *  this package reads it. Deleting it is the same act as re-pointing those
 *  fixtures at IntegrationStatusLine, which this change did not reach. */
export type RevolutWizardStatus = IntegrationStatusLine;

/** This save's operational inputs beyond the credential, derived from the
 *  revolutLive contract variant (the superset — applePayDomain is
 *  live-only) so a field rename or removal on the canonical contract
 *  surfaces here as a compile error. */
export type RevolutOperationalFields = Partial<
	Pick<
		Extract<SaveIntegration.SaveIntegrationInput, { id: "revolutLive" }>,
		"activate" | "publicKey" | "currency" | "applePayDomain"
	>
>;

export const revolutSaveIntent$ = new Subject<
	{
		/** The organization the wizard opened under; a save after a switch
		 *  names it, so it lands there or nowhere. */
		readonly orgId: string | null;
		readonly envId: RevolutEnvId;
		readonly secretKey?: string;
	} & RevolutOperationalFields
>();

export const revolutSaveCanceled$ = new Subject<{
	orgId: string | null;
	envId: RevolutEnvId;
}>();
